// Sparkline / AreaChart — минимальные SVG-чарты без зависимостей.
// Портировано из design_ref/src/Charts.jsx.

import { For } from 'solid-js'
import type { Component } from 'solid-js'

type SparklineProps = {
  data: number[]
  w?: number
  h?: number
  color?: string
}

export const Sparkline: Component<SparklineProps> = (props) => {
  const w = () => props.w ?? 120
  const h = () => props.h ?? 36
  const color = () => props.color ?? 'var(--accent)'

  const geom = () => {
    const data = props.data
    const W = w()
    const H = h()
    const max = Math.max(...data)
    const min = Math.min(...data)
    const range = max - min || 1
    const step = W / (data.length - 1)
    const pts = data.map((v, i): [number, number] => [i * step, H - ((v - min) / range) * (H - 4) - 2])
    const path = pts.map(([x, y], i) => (i ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1)).join(' ')
    const area = path + ` L ${W} ${H} L 0 ${H} Z`
    return { path, area }
  }

  return (
    <svg
      viewBox={`0 0 ${w()} ${h()}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: `${h()}px`, display: 'block' }}
    >
      <path d={geom().area} fill={color()} fill-opacity="0.08" />
      <path d={geom().path} fill="none" stroke={color()} stroke-width="1.4" />
    </svg>
  )
}

type AreaChartProps = {
  data: number[]
  h?: number
  color?: string
}

export const AreaChart: Component<AreaChartProps> = (props) => {
  const w = 600
  const h = () => props.h ?? 160
  const color = () => props.color ?? 'var(--accent)'

  const geom = () => {
    const data = props.data
    const H = h()
    const max = Math.max(...data)
    const min = Math.min(...data)
    const range = max - min || 1
    const step = w / (data.length - 1)
    const pts = data.map((v, i): [number, number] => [i * step, H - ((v - min) / range) * (H - 24) - 12])
    const path = pts.map(([x, y], i) => (i ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1)).join(' ')
    const area = path + ` L ${w} ${H - 4} L 0 ${H - 4} Z`
    const last = pts[pts.length - 1]
    const gridY = [0.25, 0.5, 0.75].map((p) => H - p * (H - 24) - 12)
    return { path, area, last, gridY }
  }

  return (
    <svg class="chart" viewBox={`0 0 ${w} ${h()}`} preserveAspectRatio="none">
      <For each={geom().gridY}>
        {(y) => (
          <line x1="0" x2={w} y1={y} y2={y} stroke="var(--line-soft)" stroke-width="1" stroke-dasharray="2 4" />
        )}
      </For>
      <path d={geom().area} fill={color()} fill-opacity="0.10" />
      <path d={geom().path} fill="none" stroke={color()} stroke-width="1.5" />
      <circle cx={geom().last[0]} cy={geom().last[1]} r="3" fill="var(--bg-surface)" stroke={color()} stroke-width="1.5" />
    </svg>
  )
}

// Псевдо-случайный детерминированный ряд (LCG). Используется как заглушка
// телеметрии; реальные ряды берутся с бэка.
export const series = (seed: number, n = 60, base = 100, vol = 10): number[] => {
  const out: number[] = []
  let v = base
  let s = seed
  for (let i = 0; i < n; i++) {
    s = (s * 9301 + 49297) % 233280
    v += (s / 233280 - 0.5) * vol
    out.push(v)
  }
  return out
}
